import { useMemo } from "react";
import { Flex, Text, Button } from "@radix-ui/themes";
import * as Tooltip from "@radix-ui/react-tooltip";
import { useQuery } from "react-query";

import { useInteractionInTrainingApi } from "@/v2/entities/interaction-in-training/InteractionInTraining.api";
import {
  ConversationInTraining,
  InteractionInTrainingStatus,
  TicketInTraining,
} from "@/v2/entities/interaction-in-training/InteractionInTraining.model";
import { useSimulator } from "../../context/SimulatorWrapper";
import styles from "../../styles.module.scss";

import { TopicBadge } from "./TopicBadge";
import { TopicBadge as TopicBadgeType } from "./TopicsList.types";

interface TopicListItemProps {
  topic: string;
  badges: {
    badges: TopicBadgeType[];
    isLive: boolean;
    isNew: boolean;
    hasUpdates: boolean;
    sectionBadges: {
      brief: TopicBadgeType | null;
      instructions: TopicBadgeType | null;
    };
  };
  isSelected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  isCollapsed?: boolean;
}

export const TopicListItem = ({
  topic,
  badges,
  isSelected,
  onSelect,
  disabled = false,
  isCollapsed = false,
}: TopicListItemProps) => {
  const interactionInTrainingApi = useInteractionInTrainingApi();
  const { simulator } = useSimulator();

  const topicName = useMemo(() => {
    return topic.trim();
  }, [topic]);

  const { data: interactionInTraining } = useQuery({
    queryKey: ["interaction-in-training", topicName],
    queryFn: () => interactionInTrainingApi.getInteractionsByTopic(topicName),
    enabled: !!topicName,
  });

  const pendingCount: number | null = useMemo(() => {
    if (!interactionInTraining) return null;

    const { tickets, conversations } = interactionInTraining;
    const channel = simulator?.channel;
    if (channel === "autonomous_quackchat") {
      return (
        conversations?.filter(
          (conversation) =>
            conversation.status === InteractionInTrainingStatus.PENDING
        ).length || 0
      );
    }

    return (
      tickets?.filter(
        (ticket) => ticket.status === InteractionInTrainingStatus.PENDING
      ).length || 0
    );
  }, [interactionInTraining]);

  return (
    <Flex gap="2">
      <Flex
        justify="between"
        align="center"
        minWidth="240px"
        className={`${styles.topicItem} ${
          isSelected ? styles.topicItemActive : ""
        } flex-grow`}
        onClick={disabled ? undefined : onSelect}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <Flex align="center" gap="2">
          <Text size="2" className={isCollapsed ? "hidden" : ""}>
            {topic}
          </Text>
          {pendingCount !== null && pendingCount > 0 && !isCollapsed && (
            <Tooltip.Provider delayDuration={100}>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <div className={styles.pendingDot}>{pendingCount}</div>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    className={styles.tooltipContent}
                    sideOffset={4}
                    side="right"
                  >
                    <Text
                      size="1"
                      style={{
                        fontSize: "11px",
                        lineHeight: 1,
                      }}
                    >
                      {pendingCount} pending review
                      {pendingCount !== 1 ? "s" : ""}
                    </Text>
                    <Tooltip.Arrow className={styles.tooltipArrow} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          )}
        </Flex>
        <Flex gap="2" className={isCollapsed ? "hidden" : ""}>
          {badges.badges.map((badge: TopicBadgeType, index: number) => (
            <TopicBadge key={index} badge={badge} />
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};
